/**
 * Profile Completion Page
 * Shown when a signed-in account has no full name yet (it was optional at sign-up)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Button, Typography, Alert,
  TextField, CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function ProfileCompletionPage() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await api.put('/auth/profile', { full_name: fullName.trim(), email: user.email });
      // Refresh user state so profile_completed=true before navigating
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : detail) || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #424242 0%, #757575 50%, #D32F2F 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 5, borderRadius: 2 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', p: 2, bgcolor: 'primary.50', borderRadius: '50%', mb: 2 }}>
              <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Complete Your Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please enter your name to start using the application.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              required
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              sx={{ mb: 3 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Email"
              value={user?.email || ''}
              disabled
              sx={{ mb: 4 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ py: 1.5, fontWeight: 600 }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
